<template>
  <div id="content">
    <div class="container-fluid">
      <h1 class="h3 mb-2 text-gray-800">{{ isEdit ? "Editar Olimpiada" : "Crear Olimpiada" }}</h1>
      <div class="card shadow mb-4">
        <div class="card-body">
          <b-form @submit.prevent="onSubmit" v-if="show">
            <div class="row">
              <div class="col-md-6">
                <b-form-group label="Título">
                  <b-form-input v-model="form.title" required />
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group label="Slug (URL)">
                  <b-form-input v-model="form.slug" placeholder="olimpiadas-mobius-2026" required />
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group label="Descripción corta">
                  <b-form-input v-model="form.description" required />
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group label="Icono">
                  <b-form-file
                    accept="image/jpeg, image/png, image/jpg, image/svg+xml"
                    v-on:change="handleFilesUpload($event)"
                    v-model="form.icon"
                  />
                  <b-img v-if="form.icon" :src="form.icon" fluid style="max-width: 200px; margin-top: 8px" />
                </b-form-group>
              </div>
              <div class="col-12">
                <b-form-group label="Descripción detallada">
                  <b-form-textarea v-model="form.longDescription" rows="3" />
                </b-form-group>
              </div>
            </div>
            <b-button type="submit" variant="primary">Guardar</b-button>
          </b-form>
        </div>
      </div>
    </div>

    <template v-if="isEdit">
      <!-- Lecciones y prácticas: mismo module -->
      <div class="container-fluid mb-3" v-if="moduleId">
        <div class="alert alert-info mb-0">
          Lecciones y prácticas del curso 636 están en el módulo vinculado (ID {{ moduleId }}).
          <router-link :to="`/admin-panel/cursos/edit/${moduleId}`" class="ml-2">Abrir como curso</router-link>
        </div>
      </div>
      <div class="container-fluid mb-3" v-else>
        <div class="alert alert-warning mb-0">
          Estás editando la olimpiada <strong>id {{ olympicId }}</strong>, sin módulo vinculado.
          La de 2026 usa slug <code>{{ migratedSlug }}</code> y módulo <strong>636</strong>.
          <b-button size="sm" variant="warning" class="ml-2" @click="openMigratedOlympic">
            Abrir olimpiada 2026 migrada
          </b-button>
        </div>
      </div>

      <!-- Fases -->
      <div class="container-fluid">
        <div class="card shadow mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h3 class="mb-0">Fases</h3>
              <b-button size="sm" variant="primary" @click="showPhaseForm = !showPhaseForm">
                Agregar fase
              </b-button>
            </div>

            <b-form v-if="showPhaseForm" @submit.prevent="createPhase" class="border p-3 mb-3">
              <div class="row">
                <div class="col-md-4">
                  <b-form-group label="Nombre">
                    <b-form-input v-model="phaseForm.name" required />
                  </b-form-group>
                </div>
                <div class="col-md-3">
                  <b-form-group label="Criterio de avance">
                    <b-form-select v-model="phaseForm.advancementType" :options="advancementOptions" />
                  </b-form-group>
                </div>
                <div class="col-md-2" v-if="phaseForm.advancementType !== 'NONE'">
                  <b-form-group label="Valor">
                    <b-form-input v-model="phaseForm.advancementValue" type="number" step="0.01" />
                  </b-form-group>
                </div>
                <div class="col-md-3 d-flex align-items-end">
                  <b-form-checkbox v-model="phaseForm.isFinal" class="mb-3">Fase final</b-form-checkbox>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <b-form-group label="Acceso a la siguiente fase">
                    <b-form-checkbox v-model="phaseForm.requirePassToUnlockNext" switch>
                      Requiere aprobar esta fase para desbloquear la siguiente
                    </b-form-checkbox>
                    <small class="text-muted d-block mt-1">
                      Si está desactivado, los estudiantes pueden entrar a la siguiente fase sin haber pasado esta.
                    </small>
                  </b-form-group>
                </div>
              </div>
              <b-button type="submit" variant="success" size="sm">Crear fase</b-button>
            </b-form>

            <div v-for="phase in phases" :key="phase.id" class="border rounded p-3 mb-3">
              <div class="d-flex justify-content-between flex-wrap">
                <div>
                  <strong>{{ phase.name }}</strong>
                  <span v-if="phase.isFinal" class="badge badge-primary ml-2">Final</span>
                  <span v-if="phase.rankingReleased" class="badge badge-success ml-2">Ranking publicado</span>
                  <div v-if="editingPhaseId !== phase.id" class="small text-muted">
                    Criterio: {{ phase.advancementType }}
                    <span v-if="phase.advancementValue != null"> ({{ phase.advancementValue }})</span>
                  </div>
                  <span
                    v-if="phase.requirePassToUnlockNext"
                    class="badge badge-warning ml-2"
                  >
                    Requiere aprobar para siguiente fase
                  </span>
                </div>
                <div>
                  <b-button
                    v-if="editingPhaseId !== phase.id"
                    size="sm"
                    variant="outline-secondary"
                    class="mr-1"
                    @click="startEditPhase(phase)"
                  >
                    Editar fase
                  </b-button>
                  <b-button size="sm" variant="outline-primary" class="mr-1" @click="releaseRanking(phase)">
                    Liberar ranking
                  </b-button>
                  <b-button size="sm" variant="outline-danger" @click="removePhase(phase.id)">Eliminar</b-button>
                </div>
              </div>

              <b-form v-if="editingPhaseId === phase.id" @submit.prevent="savePhaseEdit(phase.id)" class="border p-3 mb-3 mt-2 bg-light">
                <div class="row">
                  <div class="col-md-4">
                    <b-form-group label="Nombre">
                      <b-form-input v-model="phaseEditForm.name" required />
                    </b-form-group>
                  </div>
                  <div class="col-md-3">
                    <b-form-group label="Criterio de avance">
                      <b-form-select v-model="phaseEditForm.advancementType" :options="advancementOptions" />
                    </b-form-group>
                  </div>
                  <div class="col-md-2" v-if="phaseEditForm.advancementType !== 'NONE'">
                    <b-form-group label="Valor">
                      <b-form-input v-model="phaseEditForm.advancementValue" type="number" step="0.01" />
                    </b-form-group>
                  </div>
                  <div class="col-md-3 d-flex align-items-end">
                    <b-form-checkbox v-model="phaseEditForm.isFinal" class="mb-3">Fase final</b-form-checkbox>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <b-form-group label="Acceso a la siguiente fase">
                      <b-form-checkbox v-model="phaseEditForm.requirePassToUnlockNext" switch>
                        Requiere aprobar esta fase para desbloquear la siguiente
                      </b-form-checkbox>
                      <small class="text-muted d-block mt-1">
                        Si está desactivado, los estudiantes pueden entrar a la siguiente fase sin haber pasado esta.
                      </small>
                    </b-form-group>
                  </div>
                </div>
                <b-button type="submit" variant="success" size="sm" class="mr-1">Guardar</b-button>
                <b-button size="sm" variant="light" @click="cancelEditPhase">Cancelar</b-button>
              </b-form>

              <h5 class="mt-3">Exámenes</h5>
              <ul class="list-unstyled">
                <li v-for="ex in phase.exams" :key="ex.id" class="d-flex justify-content-between py-1">
                  <span>{{ ex.examTitle }} <small v-if="ex.countsForRanking">(ranking)</small></span>
                  <b-button size="sm" variant="link" @click="unlinkExam(phase.id, ex.examId)">Quitar</b-button>
                </li>
              </ul>
              <b-form inline @submit.prevent="linkExam(phase.id)" class="mb-2">
                <b-form-select v-model="linkExamSelections[phase.id]" :options="examOptions" class="mr-2" style="min-width: 220px" />
                <b-form-checkbox v-model="linkExamRanking[phase.id]" class="mr-2">Cuenta para ranking</b-form-checkbox>
                <b-button type="submit" size="sm">Agregar examen</b-button>
              </b-form>

              <h5>Simulacros</h5>
              <ul class="list-unstyled">
                <li v-for="sim in phase.simulacrums" :key="sim.id" class="d-flex justify-content-between py-1">
                  <span>{{ sim.simulacrumTitle }}</span>
                  <b-button size="sm" variant="link" @click="unlinkSimulacrum(phase.id, sim.simulacrumId)">Quitar</b-button>
                </li>
              </ul>
              <b-form inline @submit.prevent="linkSimulacrum(phase.id)">
                <b-form-select v-model="linkSimSelections[phase.id]" :options="simulacrumOptions" class="mr-2" style="min-width: 220px" />
                <b-button type="submit" size="sm">Agregar simulacro</b-button>
              </b-form>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import router from "../../../../router";
import {
  getOlympicById,
  getOlympicBySlug,
  saveOlympic,
  editOlympic,
  createPhase,
  updatePhase,
  deletePhase,
  linkPhaseExam,
  unlinkPhaseExam,
  linkPhaseSimulacrum,
  unlinkPhaseSimulacrum,
  releasePhaseRanking,
} from "@/services/olympics.service";

const MIGRATED_OLYMPIC_SLUG = "olimpiadas-mobius-2026";

export default {
  data() {
    return {
      show: true,
      isEdit: false,
      olympicId: null,
      moduleId: null,
      phases: [],
      showPhaseForm: false,
      editingPhaseId: null,
      phaseEditForm: {
        name: "",
        advancementType: "TOP_N_PARTICIPANTS",
        advancementValue: 50,
        isFinal: false,
        requirePassToUnlockNext: false,
      },
      form: {
        title: "",
        description: "",
        longDescription: "",
        slug: "",
        icon: "",
        iconName: "",
      },
      phaseForm: {
        name: "",
        advancementType: "TOP_N_PARTICIPANTS",
        advancementValue: 50,
        isFinal: false,
        requirePassToUnlockNext: false,
      },
      advancementOptions: [
        { value: "NONE", text: "Ninguno" },
        { value: "MIN_CORRECT", text: "Mín. respuestas correctas" },
        { value: "MIN_POINTS_PERCENT", text: "Mín. % de puntos" },
        { value: "TOP_N_PARTICIPANTS", text: "Top N participantes" },
      ],
      examsList: [],
      simulacrumsList: [],
      linkExamSelections: {},
      linkExamRanking: {},
      linkSimSelections: {},
      migratedSlug: MIGRATED_OLYMPIC_SLUG,
    };
  },
  computed: {
    examOptions() {
      return [{ value: null, text: "Seleccionar examen" }].concat(
        this.examsList.map((e) => ({ value: e.id, text: e.title }))
      );
    },
    simulacrumOptions() {
      return [{ value: null, text: "Seleccionar simulacro" }].concat(
        this.simulacrumsList.map((s) => ({ value: s.id, text: s.title }))
      );
    },
  },
  methods: {
    handleFilesUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.form.iconName = file.name;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.form.icon = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    async loadOlympic() {
      try {
        const { data } = await getOlympicById(this.olympicId);
        this.form.title = data.title || data.description;
        this.form.description = data.description;
        this.form.longDescription = data.longDescription || "";
        this.form.slug = data.slug || "";
        this.form.icon = data.icon || "";
        this.moduleId = data.moduleId;
        this.phases = data.phases || [];
      } catch (e) {
        this.toastErr(e.response?.data?.message || "No se pudo cargar la olimpiada");
        throw e;
      }
    },
    async loadExamsAndSims() {
      const headers = { Authorization: "Bearer " + localStorage.getItem("token") };
      try {
        const [exRes, simRes] = await Promise.all([
          axios.get(import.meta.env.VITE_API_BASE_URL + "/api/exams/getExamsListforAdd/", {
            headers,
            params: { search: "" },
          }),
          axios.get(import.meta.env.VITE_API_BASE_URL + "/api/simulacrums/getSimulacrumsListforAdd/", {
            headers,
            params: { search: "" },
          }),
        ]);
        this.examsList = exRes.data || [];
        this.simulacrumsList = simRes.data || [];
      } catch (e) {
        this.toastErr("No se pudieron cargar exámenes o simulacros para agregar");
        this.examsList = [];
        this.simulacrumsList = [];
      }
    },
    toastOk(msg) {
      this.$toastr.s(msg);
    },
    toastErr(msg) {
      this.$toastr.e(msg);
    },
    async onSubmit() {
      const loader = this.$loading.show();
      try {
        const payload = { ...this.form };
        if (!payload.description) payload.description = payload.title;
        if (this.isEdit) {
          await editOlympic(this.olympicId, payload);
          this.toastOk("Olimpiada actualizada");
          await this.loadOlympic();
        } else {
          const { data } = await saveOlympic(payload);
          this.toastOk("Olimpiada creada");
          router.push({ path: `/admin-panel/olimpiadas/edit/${data.id}` });
        }
      } catch (e) {
        this.toastErr(e.response?.data?.response === "duplicated" ? "Duplicado" : "Error al guardar");
      } finally {
        loader.hide();
      }
    },
    async createPhase() {
      const loader = this.$loading.show();
      try {
        await createPhase(this.olympicId, {
          ...this.phaseForm,
          position: this.phases.length,
        });
        this.showPhaseForm = false;
        this.phaseForm.name = "";
        this.phaseForm.requirePassToUnlockNext = false;
        await this.loadOlympic();
        this.toastOk("Fase creada");
      } catch (e) {
        this.toastErr(e.response?.data?.message || "Error al crear fase");
      } finally {
        loader.hide();
      }
    },
    async removePhase(phaseId) {
      if (!confirm("¿Eliminar esta fase?")) return;
      await deletePhase(this.olympicId, phaseId);
      await this.loadOlympic();
    },
    startEditPhase(phase) {
      this.editingPhaseId = phase.id;
      this.phaseEditForm = {
        name: phase.name,
        advancementType: phase.advancementType || "NONE",
        advancementValue: phase.advancementValue,
        isFinal: !!phase.isFinal,
        requirePassToUnlockNext: !!phase.requirePassToUnlockNext,
      };
    },
    cancelEditPhase() {
      this.editingPhaseId = null;
    },
    async savePhaseEdit(phaseId) {
      const loader = this.$loading.show();
      try {
        const payload = {
          ...this.phaseEditForm,
          advancementValue:
            this.phaseEditForm.advancementType === "NONE"
              ? null
              : Number(this.phaseEditForm.advancementValue),
        };
        await updatePhase(this.olympicId, phaseId, payload);
        this.editingPhaseId = null;
        await this.loadOlympic();
        this.toastOk("Fase actualizada");
      } catch (e) {
        this.toastErr(e.response?.data?.message || "Error al actualizar fase");
      } finally {
        loader.hide();
      }
    },
    async linkExam(phaseId) {
      const examId = this.linkExamSelections[phaseId];
      if (!examId) return;
      try {
        await linkPhaseExam(this.olympicId, phaseId, {
          examId: Number(examId),
          countsForRanking: this.linkExamRanking[phaseId] !== false,
        });
        this.linkExamSelections[phaseId] = null;
        await this.loadOlympic();
        this.toastOk("Examen agregado");
      } catch (e) {
        this.toastErr(e.response?.data?.message || "Error al agregar examen");
      }
    },
    async unlinkExam(phaseId, examId) {
      await unlinkPhaseExam(this.olympicId, phaseId, examId);
      await this.loadOlympic();
    },
    async linkSimulacrum(phaseId) {
      const simulacrumId = this.linkSimSelections[phaseId];
      if (!simulacrumId) return;
      try {
        await linkPhaseSimulacrum(this.olympicId, phaseId, Number(simulacrumId));
        this.linkSimSelections[phaseId] = null;
        await this.loadOlympic();
        this.toastOk("Simulacro agregado");
      } catch (e) {
        this.toastErr(e.response?.data?.message || "Error al agregar simulacro");
      }
    },
    async unlinkSimulacrum(phaseId, simulacrumId) {
      await unlinkPhaseSimulacrum(this.olympicId, phaseId, simulacrumId);
      await this.loadOlympic();
    },
    async releaseRanking(phase) {
      if (!confirm(`¿Publicar ranking de "${phase.name}"?`)) return;
      await releasePhaseRanking(this.olympicId, phase.id);
      await this.loadOlympic();
      this.toastOk("Ranking liberado");
    },
    async openMigratedOlympic() {
      try {
        const { data } = await getOlympicBySlug(MIGRATED_OLYMPIC_SLUG);
        if (!data?.id) {
          this.toastErr("No existe olimpiada con ese slug. Ejecuta migrate_olympiad_636.sql en prod.");
          return;
        }
        if (String(this.$route.params.id) === String(data.id)) {
          await this.loadOlympic();
          if (!this.moduleId) {
            this.toastErr("Sin módulo 636. Ejecuta migrate_olympiad_636.sql en la RDS de prod.");
          }
          return;
        }
        await this.$router.push({ name: "OlympicsEdit", params: { id: data.id } });
      } catch (e) {
        this.toastErr(e.response?.data?.message || "No se encontró la olimpiada migrada");
      }
    },
    async loadRouteOlympic() {
      const id = this.$route.params.id;
      if (!id) {
        this.isEdit = false;
        this.olympicId = null;
        this.moduleId = null;
        this.phases = [];
        return;
      }
      this.olympicId = id;
      this.isEdit = true;
      await this.loadOlympic();
    },
  },
  watch: {
    "$route.params.id": {
      immediate: false,
      handler() {
        this.loadRouteOlympic();
      },
    },
  },
  async mounted() {
    await this.loadExamsAndSims();
    await this.loadRouteOlympic();
  },
};
</script>
